import React from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";

import "Common.css";
import "./PricingEngineIndex.css";

import BaseElem from "BaseElem";
import PricingEngineList from "./PricingEngineVisitList";
import { PricingEngineDisplay } from "./PricingEngineDisplay";
import { PricingEngineSummary } from "./PricingEngineSummary";
import {
  getDraftVisits,
  getUpdateData,
  isHomeMedicalDisabled,
  isTherapyDisabled,
} from "Components/ListView/VisitDraftsListView";
import { convertToCrTimezone, parseDate } from "Util/DateUtils";
import Api, { showSnackbar } from "Api";
import {
  API_VAL_PRICING_GENERATE,
  API_VAL_GET_BREAKDOWN,
  API_VAL_CONFIRM_BOOKING,
  UNDEF,
  MSG_SHOW_CALENDAR_VIEW,
  MSG_FETCH_VISITS,
  MSG_SHOW_UPDATE_REVIEW,
  API_DRAFTS_DELETE,
  MSG_REFRESH_CR_INFO,
} from "Constants";
import {
  CUSTOMER_REQUEST_DATE,
  INITIATED_BY,
  REASON_FOR_CHANGE,
  REASON_FOR_CHANGE_DETAILS,
} from "Components/Dialogs/DialogCompUpdateVisit";
import {
  GeneratePricingResponse,
  Package,
  PayOnCall,
  SelectedPricing,
  Surcharge,
  Visit,
  VisitBreakdownResponse,
} from "../../@types/PricingModels";
import { isBlank, isObjEmpty, postMessage } from "Util/Utilities";
import {
  DRAFT_TYPE_NEW,
  DRAFT_TYPE_PENDING_PAYMENT,
  DRAFT_TYPE_UPDATE,
} from "Actions/VisitDraftActions";
import {
  MatchingSuccessPopup,
  PayThemselvesMode,
  PayThemselvesSuccessPopup,
  PEConfirmPopup,
} from "./PEConfirmPopup";
import { stripePromise } from "Screens/CareOwner/SubComponents/NewCareRecipient/IPCAScheduling";
import { Elements } from "@stripe/react-stripe-js";
import {
  APP_COUNTRY,
  COMPANIONSHIP,
  companionship_description,
  home_medical,
  home_medical_details,
  home_rehabilitation,
  is_nurse_requested,
  is_nurse_required,
  meal_requirements,
  MEDICAL_ESCORT,
  medical_escort_details,
  nursing_procedure,
  requested_services,
  therapy_details,
  visit_schedule,
} from "DataConstants";
import moment from "moment";

type PricingEngineIndexProps = {
  recipientStore?: any;
  visitDraftStore?: any;
  coId?: string;
  crInfo?: any;
  match: any;
};

type PricingEngineIndexConnectProps = {};

type PricingEngineIndexState = {
  firstVisit?: any;
  visits?: any[];
  isUpdate?: boolean; // is updating visits

  // api responses
  pricing?: GeneratePricingResponse;
  breakdown?: VisitBreakdownResponse;

  selectedPricing: SelectedPricing;
  selectedPackage?: Package;
  paymentInstructions?: string;

  urgentSurcharge?: Surcharge;
  transportSurcharge?: Surcharge;

  showCreateVisitDialog?: boolean;
  creatingVisits?: boolean;
  confirmPopupOpen?: boolean;
  payThemselvesSuccess?: boolean; // pay themselves success popup

  urgentVisitsCount: number | undefined;

  draftsType: string;
  paymentDraftId?: string;
};

type PricingEngineIndexActualProps = PricingEngineIndexProps &
  PricingEngineIndexConnectProps &
  RouteComponentProps<any>;

const incidentCenter = window.location.search.includes(
  "source=incident-centre"
);
class PricingEngineIndex extends BaseElem<
  PricingEngineIndexActualProps,
  PricingEngineIndexState
> {
  // earliest start time for visits
  firstVisitStartTime = new Date();

  constructor(props: PricingEngineIndexActualProps) {
    super(props);
    const self = this;

    const vd = (v: any) => parseDate(v[visit_schedule].date_from).getTime();
    // sorting first is on purpose (need the original list to be sorted as well)
    let sorted = (getDraftVisits(props) || [])
      .sort((a: any, b: any) => vd(a) - vd(b))
      .filter((a: any) => a.selected);

    // Remove unused attributes from visits
    sorted = self._cleanVisits(sorted);

    self.firstVisitStartTime = parseDate(sorted[0]?.[visit_schedule].date_from);

    const visitDraftStore = props.visitDraftStore;
    const draftsType = visitDraftStore.draftsType;
    const isUpdatingVisits = draftsType === DRAFT_TYPE_UPDATE;
    const paymentDraftId =
      draftsType === DRAFT_TYPE_PENDING_PAYMENT
        ? visitDraftStore.draftsId
        : UNDEF;

    self.state = {
      selectedPricing: {},
      firstVisit: sorted[0],
      visits: sorted,
      urgentVisitsCount: 0, //self._getUrgentVisitCount(sorted),
      isUpdate: isUpdatingVisits,
      paymentInstructions: self._getInitialInstructions(sorted[0]),
      draftsType,
      paymentDraftId,
    };
  }

  _getInitialInstructions(firstVisit: any) {
    const recipientStore = this.props.recipientStore;
    const details = recipientStore && recipientStore.careRecipient;
    let instructionFromCR;
    if (details && details.cr) {
      instructionFromCR = details.cr.payment_instruction;
    }
    return firstVisit?.payment_instruction || instructionFromCR || "";
  }

  componentDidMount() {
    this._generatePricing();
  }

  // Urgent visit calculation
  /*_getUrgentVisitCount(visits: Visit[]) {
    const curDate = convertTZ(
      new Date(),
      TIMEZONES[APP_COUNTRY || "Singapore"]
    );
    let urgentVisits = 0;
    for (let k = visits.length; k--; ) {
      // OPS working time 7am - 9pm (7 to 21)
      // urgent visit is within less than 8 working hours
      // 10 hour blackout
      const dateFrom = visits[k].visit_schedule?.date_from;
      if (!dateFrom) continue;

      const vsd = parseDate(dateFrom);

      // potential urgent visit
      if (dateDiffHours(vsd, curDate) <= 18) {
        let curHours = curDate.getHours();
        const visitStartHours = vsd.getHours();
        let hours = 0;
        while (curHours !== visitStartHours) {
          curHours = (curHours + 1) % 24;
          hours += curHours >= 7 && curHours <= 21 ? 1 : 0;
        }
        urgentVisits += hours <= 8 ? 1 : 0;
      }
    }
    return urgentVisits;
  }*/

  // Remove unused attributes from visits
  _cleanVisits(visits: Visit[]) {
    const removeNulls = (obj: any) => {
      Object.keys(obj).forEach((key) => {
        if (obj[key] === null) {
          delete obj[key];
        } else if (typeof obj[key] === "object") {
          removeNulls(obj[key]);
        }
      });
    };

    const cleaned = visits.map((v) => {
      const copy = JSON.parse(JSON.stringify(v)) as Visit;

      if (!copy[is_nurse_requested]) copy[is_nurse_requested] = false;
      if (!copy[is_nurse_required]) copy[is_nurse_required] = false;

      const selectedAdls = copy[requested_services] || [];
      //const therapyDetails: TherapyDetails = copy[therapy_details] || {};
      const htDisabled = isTherapyDisabled(copy);
      const hmDisabled = isHomeMedicalDisabled(copy);

      //delete therapyDetails[blob_urls];

      if (!selectedAdls.includes(COMPANIONSHIP)) {
        delete copy[companionship_description];
      }
      if (!selectedAdls.includes(MEDICAL_ESCORT)) {
        delete copy[medical_escort_details];
      }

      if (htDisabled) {
        delete copy[home_rehabilitation];
        delete copy[therapy_details];
      } else {
        delete copy[home_medical];
        delete copy[home_medical_details];
        delete copy[requested_services];
        delete copy[nursing_procedure];
        delete copy[meal_requirements];
      }

      if (hmDisabled) {
        delete copy[home_medical];
        delete copy[home_medical_details];
      } else {
        delete copy[home_rehabilitation];
        delete copy[therapy_details];
        delete copy[requested_services];
        delete copy[nursing_procedure];
        delete copy[meal_requirements];
      }

      delete copy.startDate;
      delete copy.endDate;
      delete copy.title;
      delete copy.selected;

      removeNulls(copy);
      return copy;
    });

    return cleaned;
  }

  _deleteDrafts() {
    const self = this;
    const state = self.state;
    if (state.draftsType !== DRAFT_TYPE_NEW) return;

    const visitIds = (state.visits || []).map((v) => v._id);
    const reqBody = {
      visit_draft_ids: visitIds,
    };

    Api.POST(API_DRAFTS_DELETE, reqBody, (response) => {
      console.log("API_DELETE_DRAFT_RESP", response);
    });
  }

  formatPreferredCareProPayloadData = (givenVisit: any) => {



    const hasPreferredCPsList = Object.hasOwn(givenVisit, 'preferred_cps_list_by_service');
    const enabledPreferredCPsList = givenVisit?.preferred_cps;

    console.log('givenVisit', givenVisit)
    console.log('enabledPreferredCPsList should be false:', enabledPreferredCPsList)

    if (!hasPreferredCPsList) {
      return {};
    };

    if (!enabledPreferredCPsList) {
      return { preferred_cps_list_by_service: [] };
    };

    const givenVisitClone = JSON.parse(JSON.stringify(givenVisit));
    const { preferred_cps_list_by_service } = givenVisitClone;

    const listOfIDs: Array<string> = [];
    const personal_care = preferred_cps_list_by_service.home_personal_care;
    const therapy = preferred_cps_list_by_service.home_therapy;
    const medical = preferred_cps_list_by_service.home_medical;

    if (personal_care) {
      personal_care.forEach((carepro: any) => listOfIDs.push(carepro.id));
    };
    if (therapy) {
      therapy.forEach((carepro: any) => listOfIDs.push(carepro.id));
    };
    if (medical) {
      medical.forEach((carepro: any) => listOfIDs.push(carepro.id));
    };

    return { preferred_cps_list_by_service: listOfIDs };
  };

  _getGeneratePricingReqBody() {
    const self = this;
    const props = self.props;
    const crInfo = props.crInfo;
    const vwoId = ((crInfo?.vwo || [])[0] || {})._id;
    let visits: any = [];
    const updateData = getUpdateData(props);
    if (this.state.isUpdate) {
      visits = self.state.visits?.map((visit) => ({
        ...visit,
        ...self.formatPreferredCareProPayloadData(visit),
      }));
    } else {
      visits = self.state.visits?.map((visit) => ({
        ...visit,
        visit_schedule: {
          date_from: convertToCrTimezone(
            visit.visit_schedule.date_from,
            this.getRegion()
          ),
          date_to: convertToCrTimezone(
            visit.visit_schedule.date_to,
            this.getRegion()
          ),
        },
        ...self.formatPreferredCareProPayloadData(visit),
      }));
    }
    const req: any = {
      cr_id: crInfo?._id,
      co_id: props.coId,
      visits,
    };

    console.log('>>> req', req)

    vwoId && (req.vwo_id = vwoId);
    if (updateData) {
      req.visits_update_info = {
        reason_for_change: updateData[REASON_FOR_CHANGE],
        reason_for_change_details: updateData[REASON_FOR_CHANGE_DETAILS],
        customer_request_date: updateData[CUSTOMER_REQUEST_DATE],
        initiated_by: updateData[INITIATED_BY],
      };
    }

    const paymentDraftId = self.state.paymentDraftId;
    if (paymentDraftId) {
      req.payment_draft_id = paymentDraftId;
    }

    return req;
  }

  _getBreakdownReqBody() {
    const self = this;
    const state = self.state;
    const selectedPricing = state.selectedPricing;
    const urgent = state.urgentSurcharge;
    const selUrgent = selectedPricing.urgent_surcharge;

    const transport = state.transportSurcharge;
    const selTransport = selectedPricing.transport_surcharge;

    const packageId = selectedPricing.package_id;
    const paygPackageId = selectedPricing.pay_as_you_go_package_id;
    const purchasedIds = selectedPricing.purchased_package_ids;

    const pricing: SelectedPricing = {};

    if (packageId) {
      pricing.package_id = packageId;
    } else if (paygPackageId) {
      pricing.pay_as_you_go_package_id = paygPackageId;
    } else if (purchasedIds) {
      pricing.purchased_package_ids = purchasedIds;
    }

    urgent && (pricing.urgent_surcharge = selUrgent);
    transport && (pricing.transport_surcharge = selTransport);

    const generatePricingReqBody = self._getGeneratePricingReqBody();
    const requestBody: any = {
      payment_instruction: state.paymentInstructions,
      visits_info: generatePricingReqBody,
      selected_pricing: pricing,
      visits_update_info: generatePricingReqBody.visits_update_info,
    };

    return requestBody;
  }

  _getConfirmApiReqBody(payOnCall?: PayOnCall) {
    const self = this;
    const reqBody = self._getBreakdownReqBody();
    if (payOnCall) {
      reqBody.pay_on_call = payOnCall;
    }
    return reqBody;
  }

  _generatePricing() {
    const self = this;
    const state = self.state as PricingEngineIndexState;
    const request = self._getGeneratePricingReqBody();

    Api.POST(
      API_VAL_PRICING_GENERATE,
      request,
      (resp) => {
        let d = resp?.data;
        let data: GeneratePricingResponse = d?.data || d;

        if (!data || !data.pay_as_you_go) return;

        if (
          isObjEmpty(data.pay_as_you_go) &&
          isBlank(data.owned_packages) &&
          isBlank(data.other_packages)
        ) {
          showSnackbar(
            "Faild to generate pricing for the selected visit(s)",
            "error",
            3000
          );
          return;
        }

        const initialSelection = data?.initial_package_selection;

        // Direct state update to avoid re-render
        state.pricing = data;
        state.selectedPricing = {};

        if (initialSelection && initialSelection[0]) {
          const id = initialSelection[0];
          const pkg = (data.owned_packages || []).find(
            (p) => p.purchased_id === id
          );
          pkg && self._onPackageSelect(pkg);
        } else {
          self._onPackageSelect(data.pay_as_you_go);
        }
      },
      true,
      true,
      true
    );
  }

  // Get pricing breakdown
  _applyPricing = () => {
    const self = this;
    const request = self._getBreakdownReqBody();

    Api.POST(
      API_VAL_GET_BREAKDOWN,
      request,
      (resp) => {
        const data =
          resp?.data as VisitBreakdownResponse;

        self.setState({ breakdown: data });
      },
      true
    );
  };

  // "Continue" when updating visits
  // "Confirm" when scheduling visits
  _confirmPricing = () => {
    // check if visit is too close, disable the create/update flow

    const visitDrafts = getDraftVisits(this.props);
    visitDrafts.forEach((draft: any) => {
      const isHPC = this.props.location.pathname.includes("/hpc");
      if (
        isHPC &&
        (!draft.status ||
          draft.status.visit === "Open" ||
          draft.status.visit === "Requested")
      ) {
        const dateFrom = draft[visit_schedule]["date_from"];
        const currentTime = moment();
        const startTime = moment(dateFrom);
        const cutOffTime =
          this.props.recipientStore?.careRecipient?.co?.visit_too_close_time ??
          1;
        if (cutOffTime) {
          const hoursDiff = startTime.diff(currentTime, "hours");
          const addCuttOffTimeInCurrentTime = currentTime.add(
            cutOffTime * 60 + 15, // cuttOff hours(in minutes) + 15min
            "minutes"
          );
          if (!incidentCenter && hoursDiff < cutOffTime) {
            showSnackbar(
              `Please select the visit start time after ${addCuttOffTimeInCurrentTime.format(
                "hh:mm"
              )} slot.`,
              "error",
              3000
            );
            return;
          }
        }
      }
    });

    const self = this;
    const state = self.state;
    const breakdown = state.breakdown;
    if (!breakdown) return;

    if (state.isUpdate) {
      postMessage(MSG_SHOW_UPDATE_REVIEW, {
        originalVisit: getUpdateData(self.props).originalVisit,
        breakdownRequest: self._getBreakdownReqBody(),
        breakdownResponse: breakdown,
        selectedPackage: state.selectedPackage,
      });
      return;
    }

    if (breakdown.can_immediate_confirm) {
      self._callConfirmApi(self._getConfirmApiReqBody());
    } else {
      self.setState({ confirmPopupOpen: true });
    }
  };

  _onPaymentInstChange = (_inpField: any, inst: string) => {
    this.setState({ paymentInstructions: inst });
  };
  _onPackageSelect = (pkg: Package) => {
    const self = this;
    const sp = self.state.selectedPricing;
    const spJson = JSON.stringify(sp);

    // purchased package
    if (pkg.purchased_id) {
      sp.purchased_package_ids = (
        pkg.purchased_ids_to_select_upon_click || []
      ).slice();
      delete sp.package_id;
      delete sp.pay_as_you_go_package_id;
    }
    // other packages
    else if (pkg.save) {
      sp.package_id = pkg.package_id;
      delete sp.purchased_package_ids;
      delete sp.pay_as_you_go_package_id;
    }
    // Pay as you go
    else {
      sp.pay_as_you_go_package_id = pkg.package_id;
      delete sp.package_id;
      delete sp.purchased_package_ids;
    }

    const urgent = pkg.urgent_surcharge;
    const transport = pkg.transport_surcharge;

    if (urgent) {
      const s =
        sp.urgent_surcharge ||
        ({
          id: urgent.id,
          is_waived: false,
        } as Surcharge);
      s.id = urgent.id;
      sp.urgent_surcharge = s;
    }

    if (transport) {
      const s =
        sp.transport_surcharge ||
        ({
          id: transport.id,
          is_waived: false,
        } as Surcharge);
      s.id = transport.id;
      sp.transport_surcharge = s;
      sp.transport_surcharge = s;

      /*const ts =
        sp.transport_surcharge ||
        transport.map(
          (t) =>
            ({
              id: t.id,
              is_waived: false,
            } as Surcharge)
        );
      for (let k = 0; k < ts.length && k < transport.length; k++) {
        ts[k].id = transport[k].id;
      }
      sp.transport_surcharge = ts;      
      */
    }

    // if package selection changes (clear the breakdown)
    const breakdown =
      spJson === JSON.stringify(sp) ? self.state.breakdown : UNDEF;

    self.setState({
      selectedPricing: sp,
      selectedPackage: pkg,
      urgentSurcharge: urgent,
      transportSurcharge: transport,
      breakdown: breakdown,
    });
  };

  _onSurchargeUpdate = () => {
    this.setState({ breakdown: UNDEF });
  };

  _backToCalendar = () => {
    const self = this;
    const visits = self.state.visits || [];
    const count = visits.length;
    if (!count) return;

    const firstVisitDate = parseDate(visits[0].visit_schedule.date_from);
    self.async(() => {
      postMessage(MSG_SHOW_CALENDAR_VIEW, UNDEF);
      const snackbarText = `${count} visit${count > 1 ? "s" : ""
        } created successfully`;
      postMessage(
        MSG_FETCH_VISITS,
        { message: snackbarText, date: firstVisitDate },
        300
      );
    }, 200);
  };

  _payOnCall = (params: PayOnCall) => {
    const reqBody = this._getConfirmApiReqBody(params);
    this._callConfirmApi(reqBody);
  };

  _callConfirmApi(reqBody: any) {
    if (incidentCenter) reqBody["from_incident_center"] = true;
    Api.POST(
      API_VAL_CONFIRM_BOOKING,
      reqBody,
      (resp) => {
        const data = resp?.data;

        if (data && data.success) {
          this.setState({
            confirmPopupOpen: false,
            showCreateVisitDialog: true,
            urgentVisitsCount: data.urgent_visit_count,
          });
          this._deleteDrafts();
        }
      },
      true
    );
  }

  _payThemselves = (mode: PayThemselvesMode) => {

    const self = this;
    const reqBody = self._getBreakdownReqBody();

    reqBody.pay_themselves = {
      payment_mode: mode,
    };
    if (incidentCenter) reqBody["from_incident_center"] = true;
    Api.POST(
      API_VAL_CONFIRM_BOOKING,
      reqBody,
      (resp) => {
        const data = resp?.data;

        if (data && data.success) {
          self.setState({
            payThemselvesSuccess: true,
            confirmPopupOpen: false,
          });
          self._deleteDrafts();
        }
      },
      true
    );
  };

  _payRemWithCard = () => {
    const self = this;
    const reqBody = self._getBreakdownReqBody();
    self._callConfirmApi(reqBody);
  };

  // An email has been sent to CO to complete the payment
  _onPayThemselvesClose = () => {
    postMessage(MSG_SHOW_CALENDAR_VIEW, UNDEF);
    postMessage(MSG_REFRESH_CR_INFO, UNDEF, 100);
  };

  _closePEConfirmPopup = () => {
    this.setState({
      confirmPopupOpen: false,
    });
  };

  getRegion = () => {
    const { recipientStore } = this.props;
    const timezone = recipientStore?.careRecipient?.cr?.timezone;
    let region = timezone.timezones[APP_COUNTRY];
    if (timezone?.is_single_tz === false) {
      const city = recipientStore?.careRecipient?.co?.address_list?.[0]?.city;
      region = timezone.timezones[city];
    }
    return region;
  };

  render() {
    const self = this;
    const state = self.state,
      visits = state.visits,
      firstVisit = state.firstVisit;
    const { recipientStore } = self.props;
    const details = recipientStore && recipientStore.careRecipient;
    const co = details && details.co;
    const isConcierge = !!co?.wallet?.is_concierge;
    //requestDateTime = state.requestDateTime;
    if (!firstVisit) return <div />;

    return (
      <div className="pei-whitebox">
        <div className="pei-list">
          <PricingEngineList visits={visits} region={this.getRegion()} />
        </div>
        <div className="pei-vertical-separator" />
        <div className="pei-display">
          <PricingEngineDisplay
            pricing={state.pricing}
            selectedPricing={state.selectedPricing}
            paymentInstruction={state.paymentInstructions}
            visitCount={visits?.length}
            urgentSurcharge={state.urgentSurcharge}
            transportSurcharge={state.transportSurcharge}
            onSurchargeUpdate={self._onSurchargeUpdate}
            onPaymentInstChange={self._onPaymentInstChange}
            onPackageSelect={self._onPackageSelect}
            onApplyPricing={self._applyPricing}
          />
        </div>
        <div className="pei-vertical-separator" />
        <div className="pei-summary">
          <PricingEngineSummary
            isUpdate={state.isUpdate}
            breakdown={state.breakdown}
            region={this.getRegion()}
            selectedPricing={state.selectedPricing}
            selectedPackage={state.selectedPackage}
            recipientStore={self.props.recipientStore}
            onConfirm={self._confirmPricing}
            visitsInfo={visits}
          />
        </div>

        {state.showCreateVisitDialog && (
          <MatchingSuccessPopup
            onClose={self._backToCalendar}
            urgentCount={state.urgentVisitsCount}
            visitCount={visits?.length}
          />
        )}

        {state.confirmPopupOpen && state.breakdown && (
          <Elements stripe={stripePromise}>
            <PEConfirmPopup
              breakdown={state.breakdown}
              isUpdate={state.isUpdate}
              isConcierge={isConcierge}
              selectedPackage={state.selectedPackage}
              onPayOnCallConfirm={self._payOnCall}
              onClose={self._closePEConfirmPopup}
              onPayThemselves={self._payThemselves}
              firstVisitStartTime={self.firstVisitStartTime}
              onPayRemainingWithCardContinue={self._payRemWithCard}
            />
          </Elements>
        )}

        {state.payThemselvesSuccess && (
          <PayThemselvesSuccessPopup onClose={self._onPayThemselvesClose} />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return { ...state };
};

export default withRouter(
  connect<PricingEngineIndexState, PricingEngineIndexConnectProps>(
    mapStateToProps
  )(PricingEngineIndex)
);
